import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider as TanStackRouterProvider, type NavigateOptions, type ToOptions } from "@tanstack/react-router";
import { I18nProvider as AriaI18nProvider, RouterProvider as AriaRouterProvider } from "react-aria-components";
import { EmptyState, SkeletonRows } from "@bres/ui";
import { LockKeyIcon } from "@phosphor-icons/react";
import { ApiError } from "./lib/api.ts";
import { I18nProvider, useI18n } from "./lib/i18n.tsx";
import { SessionProvider, useMeQuery } from "./lib/session.tsx";
import { PageError } from "./lib/errors.tsx";
import { Login } from "./shell/Login.tsx";
import { Invitation } from "./shell/Invitation.tsx";
import { applyDensity, applyTheme, readDensity, readTheme } from "./shell/theme.ts";
import { router } from "./router.tsx";
import "./styles/app.css";

declare module "react-aria-components" {
  interface RouterConfig {
    href: ToOptions["to"] | (string & {});
    routerOptions: Omit<NavigateOptions, keyof ToOptions>;
  }
}

applyTheme(readTheme());
applyDensity(readDensity());

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

function Gate() {
  const { t, tag } = useI18n();
  const me = useMeQuery();
  if (me.isPending) return <SkeletonRows label={t("common.loading")} rows={4} />;
  if (me.isError) return <PageError error={me.error} onRetry={() => void me.refetch()} />;
  if (window.location.pathname === "/invitation") return <Invitation me={me.data} />;
  if (!me.data.authenticated) return <Login />;
  if (!me.data.member && !me.data.platform?.admin) {
    return (
      <main className="login">
        <EmptyState icon={<LockKeyIcon />} title={t("shell.no_membership_title")}>
          {t("shell.no_membership_body")}
        </EmptyState>
      </main>
    );
  }
  return (
    <AriaI18nProvider locale={tag}>
      <AriaRouterProvider navigate={(to, options) => void router.navigate({ to: to as string, ...options })} useHref={(to) => /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(String(to)) ? String(to) : router.buildLocation({ to: to as string }).href}>
        <SessionProvider me={me.data} member={me.data.member ?? null}>
          <TanStackRouterProvider router={router} />
        </SessionProvider>
      </AriaRouterProvider>
    </AriaI18nProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <Gate />
      </I18nProvider>
    </QueryClientProvider>
  </StrictMode>,
);
